import {
  instanceAllValues,
  templateActiveFields,
  type LeagueCompetitionInstance,
  type LeagueCompetitionTemplate,
} from "./competition";
import type { LeagueDynamicFieldValue } from "./dynamicFieldValue";
import {
  UNIVERSAL_ROLE_LABELS,
  type LeagueAthleteFieldDefinition,
  type LeagueCompetitionUniversalFieldRole,
} from "./fieldDefinition";
import { trimmedNonEmpty } from "./firestore";
import { isCourseKMLRole, type GPSTrackerCourseKML } from "./gps";

// Port of LeagueHomeService.eventDisplay(instance:template:) — resolves the
// universal template field roles against the instance's dynamic values.

export interface LeagueCompetitionEventDisplay {
  instance: LeagueCompetitionInstance;
  fieldDefinitions: LeagueAthleteFieldDefinition[];
  eventName: string;
  eventCode?: string;
  eventShortName?: string;
  startsAt?: Date;
  logoURL?: string;
  /** Variant for dark backgrounds (universal role eventLogoDark); falls back to logoURL when unset. */
  logoDarkURL?: string;
  titleSponsor?: string;
  coverImageURL?: string;
  venueName?: string;
  address?: string;
  city?: string;
  region?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  eventTimeZoneIdentifier?: string;
  isGPSRace: boolean;
  gpsWindowStart?: Date;
  gpsWindowEnd?: Date;
  courseKMLs: GPSTrackerCourseKML[];
}

function isHTTPURL(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

export function eventDisplay(
  instance: LeagueCompetitionInstance,
  template: LeagueCompetitionTemplate | undefined,
): LeagueCompetitionEventDisplay {
  const values = instanceAllValues(instance);
  const fields = template ? templateActiveFields(template) : [];
  const roleFieldIDs = new Map<LeagueCompetitionUniversalFieldRole, string>();
  for (const field of fields) {
    if (field.universalCompetitionRole && !roleFieldIDs.has(field.universalCompetitionRole)) {
      roleFieldIDs.set(field.universalCompetitionRole, field.id);
    }
  }

  const valueFor = (role: LeagueCompetitionUniversalFieldRole): LeagueDynamicFieldValue | undefined => {
    const fieldID = roleFieldIDs.get(role);
    return fieldID ? values[fieldID] : undefined;
  };
  const str = (role: LeagueCompetitionUniversalFieldRole): string | undefined => {
    const value = valueFor(role);
    return value?.kind === "string" ? trimmedNonEmpty(value.value) : undefined;
  };
  const date = (role: LeagueCompetitionUniversalFieldRole): Date | undefined => {
    const value = valueFor(role);
    return value?.kind === "date" ? value.value : undefined;
  };
  const bool = (role: LeagueCompetitionUniversalFieldRole): boolean => {
    const value = valueFor(role);
    return value?.kind === "bool" ? value.value : false;
  };
  const num = (role: LeagueCompetitionUniversalFieldRole): number | undefined => {
    const value = valueFor(role);
    return value?.kind === "double" || value?.kind === "int" ? value.value : undefined;
  };
  const courseKML = (role: LeagueCompetitionUniversalFieldRole): GPSTrackerCourseKML | undefined => {
    if (!isCourseKMLRole(role)) return undefined;
    const fieldID = roleFieldIDs.get(role);
    const url = str(role);
    if (!fieldID || !url || !isHTTPURL(url)) return undefined;
    return { id: role, fieldID, role, name: UNIVERSAL_ROLE_LABELS[role], url };
  };

  let eventTimeZoneIdentifier = str("eventTimeZone");
  if (eventTimeZoneIdentifier) {
    try {
      new Intl.DateTimeFormat("en-US", { timeZone: eventTimeZoneIdentifier });
    } catch {
      eventTimeZoneIdentifier = undefined;
    }
  }

  return {
    instance,
    fieldDefinitions: fields,
    eventName: str("eventName") ?? instance.name,
    eventCode: str("eventCode"),
    eventShortName: str("eventShortName"),
    startsAt: date("eventStartDateTime"),
    logoURL: str("eventLogo"),
    logoDarkURL: str("eventLogoDark"),
    titleSponsor: str("eventTitleSponsor"),
    coverImageURL: str("eventCoverImage"),
    venueName: str("eventVenueName"),
    address: str("eventAddress"),
    city: str("eventCity"),
    region: str("eventRegion"),
    country: str("eventCountry"),
    latitude: num("eventLatitude"),
    longitude: num("eventLongitude"),
    eventTimeZoneIdentifier,
    isGPSRace: bool("gpsRace"),
    gpsWindowStart: date("gpsWindowStart"),
    gpsWindowEnd: date("gpsWindowEnd"),
    courseKMLs: [courseKML("swimKML"), courseKML("bikeKML"), courseKML("runKML"), courseKML("courseKML")].filter(
      (kml): kml is GPSTrackerCourseKML => kml !== undefined,
    ),
  };
}

const STATUS_SORT_RANK: Record<string, number> = { published: 0, draft: 1, archived: 2 };

/** Mirrors LeagueHomeService.eventDisplays sorting: status rank, then startsAt. */
export function eventDisplays(
  instances: LeagueCompetitionInstance[],
  templates: LeagueCompetitionTemplate[],
): LeagueCompetitionEventDisplay[] {
  const templatesByID = new Map(templates.map((template) => [template.id, template]));
  return instances
    .map((instance) => eventDisplay(instance, templatesByID.get(instance.templateID)))
    .sort((a, b) => {
      const rankA = STATUS_SORT_RANK[a.instance.status] ?? 3;
      const rankB = STATUS_SORT_RANK[b.instance.status] ?? 3;
      if (rankA !== rankB) return rankA - rankB;
      const timeA = a.startsAt?.getTime() ?? Number.MAX_SAFE_INTEGER;
      const timeB = b.startsAt?.getTime() ?? Number.MAX_SAFE_INTEGER;
      return timeA - timeB;
    });
}

export function locationText(display: LeagueCompetitionEventDisplay): string | undefined {
  const locality = trimmedNonEmpty(
    [display.city, display.region, display.country]
      .map((part) => trimmedNonEmpty(part))
      .filter(Boolean)
      .join(", "),
  );
  const venue = trimmedNonEmpty(display.venueName);
  if (venue && locality) return `${venue} • ${locality}`;
  return venue ?? trimmedNonEmpty(display.address) ?? locality;
}
